// GradeStep.js - Grade input step
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from '../../i18n/useTranslation';

const GradeStep = ({ value = '', onChange, onNext, onBack }) => {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const [grade, setGrade] = useState(value);

  useEffect(() => {
    setGrade(value);
  }, [value]);

  const colors = {
    bg: isDark ? '#1e293b' : '#f8fafc',
    card: isDark ? '#334155' : '#ffffff',
    text: isDark ? '#f8fafc' : '#1e293b',
    textSecondary: isDark ? '#94a3b8' : '#64748b',
    accent: '#2563eb',
    border: isDark ? '#475569' : '#e2e8f0',
    inputBg: isDark ? '#1e293b' : '#f8fafc',
  };

  const handleChange = (text) => {
    setGrade(text);
    onChange?.(text);
  };

  const isValid = grade.trim().length > 0;

  return (
    <View style={[styles.container, { backgroundColor: colors.bg }]}>
      <View style={[styles.content, { backgroundColor: colors.card, borderColor: colors.border }]}>
        {/* Icon */}
        <View style={[styles.iconContainer, { backgroundColor: colors.accent + '15' }]}>
          <MaterialCommunityIcons name="school" size={48} color={colors.accent} />
        </View>

        {/* Title */}
        <Text style={[styles.title, { color: colors.text }]}>
          {t('What grade are you in?')}
        </Text>
        <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
          {t('This helps us personalize your learning content')}
        </Text>

        {/* Input */}
        <TextInput
          style={[
            styles.input,
            {
              backgroundColor: colors.inputBg,
              borderColor: grade ? colors.accent : colors.border,
              color: colors.text,
            },
          ]}
          value={grade}
          onChangeText={handleChange}
          placeholder={t('Enter your grade (e.g., 4, 5, 6... 10, 11, 12)')}
          placeholderTextColor={colors.textSecondary}
          keyboardType="default"
          autoFocus
        />

        {/* Navigation Buttons */}
        <View style={styles.buttonRow}>
          <TouchableOpacity
            style={[styles.secondaryButton, { borderColor: colors.border }]}
            onPress={onBack}
            activeOpacity={0.7}
          >
            <MaterialCommunityIcons name="arrow-left" size={18} color={colors.text} />
            <Text style={[styles.secondaryButtonText, { color: colors.text }]}>{t('Back')}</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[
              styles.primaryButton,
              { backgroundColor: isValid ? colors.accent : colors.border },
            ]}
            onPress={onNext}
            activeOpacity={0.8}
            disabled={!isValid}
          >
            <Text style={[styles.primaryButtonText, { opacity: isValid ? 1 : 0.5 }]}>
              {t('Continue')}
            </Text>
            <MaterialCommunityIcons
              name="arrow-right"
              size={18}
              color="#fff"
              style={{ opacity: isValid ? 1 : 0.5 }}
            />
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  content: {
    width: '100%',
    maxWidth: 440,
    borderRadius: 16,
    padding: 32,
    borderWidth: 1,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 4,
  },
  iconContainer: {
    width: 88,
    height: 88,
    borderRadius: 44,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 24,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 15,
    textAlign: 'center',
    marginBottom: 28,
  },
  input: {
    width: '100%',
    borderWidth: 2,
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 14,
    fontSize: 18,
    textAlign: 'center',
    marginBottom: 28,
  },
  buttonRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
    width: '100%',
  },
  secondaryButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 10,
    borderWidth: 1,
    gap: 6,
  },
  secondaryButtonText: {
    fontSize: 15,
    fontWeight: '500',
  },
  primaryButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 10,
    gap: 6,
  },
  primaryButtonText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '600',
  },
});

export default GradeStep;
